// ============================================================
// DrawerCrearEstudiante — formulario lateral para crear estudiante
// dentro del Asistente de Matrícula. No reemplaza UsuarioFormPage,
// es una versión simplificada con los campos esenciales.
// ============================================================

import React, { useState } from 'react';
import { usuarioService } from 'services/usuarioService';
import { UsuarioPayload, UsuarioListItem, ROL_ID } from 'types/usuario.types';
import { catalogStorage } from 'utils/catalogStorage';
import { getConfig } from 'utils/appConfig';
import { I } from 'components/ui';

interface Props {
  open:      boolean;
  onClose:   () => void;
  onCreated: (u: UsuarioListItem) => void;
  /** Pre-rellena el username/email con esto si lo pasaron al buscar */
  initialQuery?: string;
}

interface FormState {
  nombres:         string;
  apellidos:       string;
  dni:             string;
  codigoSiagie:    string;
  fechaNacimiento: string;
  telefono:        string;
  direccion:       string;
  email:           string;     // solo parte local
  password:        string;
  sexo:            '' | 'M' | 'F';
}

const initialForm: FormState = {
  nombres: '', apellidos: '', dni: '', codigoSiagie: '',
  fechaNacimiento: '', telefono: '', direccion: '',
  email: '', password: '', sexo: '',
};

const DrawerCrearEstudiante: React.FC<Props> = ({ open, onClose, onCreated, initialQuery }) => {
  const [form, setForm]           = useState<FormState>(initialForm);
  const [errors, setErrors]       = useState<Partial<Record<keyof FormState, string>>>({});
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError]   = useState<string | null>(null);

  const dominio = getConfig().correodominio || '@institucion.edu.pe';

  // Reset al abrir/cerrar y pre-rellenar
  React.useEffect(() => {
    if (open) {
      setForm({
        ...initialForm,
        // Si la búsqueda parecía un DNI (8 dígitos), pre-rellenamos DNI
        dni: initialQuery && /^\d{8}$/.test(initialQuery) ? initialQuery : '',
      });
      setErrors({});
      setApiError(null);
    }
  }, [open, initialQuery]);

  const setField = (k: keyof FormState, v: string) => {
    setForm(p => ({ ...p, [k]: v }));
    setErrors(p => { const n = { ...p }; delete n[k]; return n; });
    setApiError(null);
  };

  const validate = (): boolean => {
    const e: Partial<Record<keyof FormState, string>> = {};
    if (!form.nombres.trim())   e.nombres   = 'Requerido';
    if (!form.apellidos.trim()) e.apellidos = 'Requerido';
    if (!/^\d{8}$/.test(form.dni)) e.dni    = 'Debe tener 8 dígitos';
    if (!form.email.trim())     e.email     = 'Requerido';
    else if (!/^[a-z0-9._-]+$/i.test(form.email.trim()))
      e.email = 'Solo letras, números, puntos, guiones';
    if (form.password.length < 6) e.password = 'Mínimo 6 caracteres';
    if (form.sexo !== 'M' && form.sexo !== 'F') e.sexo = 'Selecciona el sexo';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    setApiError(null);
    try {
      const username = form.email.trim();
      const idRol = catalogStorage.getIdRol('ESTUDIANTE', ROL_ID.ESTUDIANTE);
      const payload: UsuarioPayload = {
        username,
        password:        form.password,
        nombres:         form.nombres.trim(),
        apellidos:       form.apellidos.trim(),
        email:           `${username}${dominio}`,
        dni:             form.dni,
        telefono:        form.telefono,
        direccion:       form.direccion || null,
        fechaNacimiento: form.fechaNacimiento || null,
        codigoSiagie:    form.codigoSiagie.trim() || null,
        fotoPerfil:      null,
        idRol,
        activo:          true,
        sexo:            form.sexo === '' ? null : form.sexo,
      };
      const creado = await usuarioService.crear(payload);
      onCreated(creado);
    } catch (e: unknown) {
      setApiError(e instanceof Error ? e.message : 'Error al crear el estudiante');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[150]"
        onClick={onClose}
        style={{ animation: 'fade-in .2s ease' }}
      />

      {/* Drawer */}
      <aside
        className="fixed top-0 right-0 bottom-0 w-full max-w-md bg-white shadow-2xl z-[160]
                   flex flex-col"
        style={{
          animation: 'slide-in-right .25s cubic-bezier(.16,1,.3,1)',
        }}
      >
        {/* Cabecera */}
        <div
          className="px-6 py-5 text-white relative overflow-hidden flex items-center justify-between"
          style={{ background: 'linear-gradient(135deg, #1e1b4b 0%, #4338ca 50%, #0d9488 100%)' }}
        >
          <div className="flex-1 min-w-0">
            <h2 className="text-[17px] font-extrabold m-0" style={{ letterSpacing: '-.01em' }}>
              Nuevo Estudiante
            </h2>
            <p className="text-white/70 text-xs mt-0.5 m-0">
              Datos esenciales para registrar el estudiante
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-white/80 hover:text-white p-1.5 rounded-r-sm hover:bg-white/10 transition-colors flex-shrink-0"
            aria-label="Cerrar"
          >
            <I.X size={20} />
          </button>
        </div>

        {/* Body con scroll interno */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid gap-4">
            {/* Nombres y apellidos */}
            <div className="grid grid-cols-2 gap-3">
              <div className="field">
                <label>Nombres <span className="required">*</span></label>
                <input
                  className="input"
                  value={form.nombres}
                  onChange={e => setField('nombres', e.target.value)}
                  placeholder="Juan Carlos"
                  disabled={submitting}
                />
                {errors.nombres && <p className="text-[11px] text-red-600">{errors.nombres}</p>}
              </div>
              <div className="field">
                <label>Apellidos <span className="required">*</span></label>
                <input
                  className="input"
                  value={form.apellidos}
                  onChange={e => setField('apellidos', e.target.value)}
                  placeholder="Pérez Quispe"
                  disabled={submitting}
                />
                {errors.apellidos && <p className="text-[11px] text-red-600">{errors.apellidos}</p>}
              </div>
            </div>

            {/* DNI y fecha */}
            <div className="grid grid-cols-2 gap-3">
              <div className="field">
                <label>DNI <span className="required">*</span></label>
                <input
                  className="input"
                  value={form.dni}
                  onChange={e => setField('dni', e.target.value.replace(/\D/g, '').slice(0, 8))}
                  placeholder="12345678"
                  inputMode="numeric"
                  maxLength={8}
                  disabled={submitting}
                />
                {errors.dni && <p className="text-[11px] text-red-600">{errors.dni}</p>}
              </div>
              <div className="field">
                <label>Fecha de nacimiento</label>
                <input
                  type="date"
                  className="input"
                  value={form.fechaNacimiento}
                  onChange={e => setField('fechaNacimiento', e.target.value)}
                  disabled={submitting}
                />
              </div>
            </div>

            {/* Sexo (obligatorio para estudiantes) */}
            <div className="field">
              <label>Sexo <span className="required">*</span></label>
              <select
                className="input"
                value={form.sexo}
                onChange={e => setField('sexo', e.target.value as '' | 'M' | 'F')}
                disabled={submitting}
              >
                <option value="">— Selecciona —</option>
                <option value="M">Masculino</option>
                <option value="F">Femenino</option>
              </select>
              {errors.sexo && <p className="error-text">{errors.sexo}</p>}
            </div>

            {/* Código SIAGIE (opcional) */}
            <div className="field">
              <label>Código SIAGIE <span className="font-normal normal-case text-ink-400">(opcional)</span></label>
              <input
                className="input"
                value={form.codigoSiagie}
                onChange={e => setField('codigoSiagie', e.target.value)}
                placeholder="0012345678"
                inputMode="numeric"
                disabled={submitting}
              />
              <p className="help-text">Puedes asignarlo después desde el listado de matrículas</p>
            </div>

            {/* Email partido + dominio */}
            <div className="field">
              <label>Email institucional <span className="required">*</span></label>
              <div className="flex">
                <input
                  className="input rounded-r-none"
                  value={form.email}
                  onChange={e => setField('email', e.target.value.toLowerCase())}
                  placeholder="jperez"
                  disabled={submitting}
                  style={{ flex: 1 }}
                />
                <span
                  className="px-3 py-2 text-[13px] text-ink-500 border border-l-0 rounded-r-[10px] bg-surface-alt whitespace-nowrap flex items-center"
                  style={{ borderColor: 'var(--border-strong)' }}
                >
                  {dominio}
                </span>
              </div>
              {errors.email && <p className="text-[11px] text-red-600">{errors.email}</p>}
              <p className="help-text">El usuario también será este (sin el dominio)</p>
            </div>

            {/* Contraseña */}
            <div className="field">
              <label>Contraseña <span className="required">*</span></label>
              <input
                type="text"
                className="input"
                value={form.password}
                onChange={e => setField('password', e.target.value)}
                placeholder="Mínimo 6 caracteres"
                disabled={submitting}
              />
              {errors.password && <p className="text-[11px] text-red-600">{errors.password}</p>}
              <p className="help-text">El estudiante podrá cambiarla en su primer ingreso</p>
            </div>

            {/* Datos opcionales */}
            <div className="grid grid-cols-2 gap-3">
              <div className="field">
                <label>Teléfono</label>
                <input
                  className="input"
                  value={form.telefono}
                  onChange={e => setField('telefono', e.target.value)}
                  placeholder="999 888 777"
                  disabled={submitting}
                />
              </div>
              <div className="field">
                <label>Dirección</label>
                <input
                  className="input"
                  value={form.direccion}
                  onChange={e => setField('direccion', e.target.value)}
                  placeholder="Av. ..."
                  disabled={submitting}
                />
              </div>
            </div>

            {apiError && (
              <div
                className="rounded-r-sm border border-red-200 px-3 py-2.5 text-[13px]"
                style={{ background: 'var(--danger-bg)', color: '#991b1b' }}
              >
                {apiError}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div
          className="px-6 py-4 border-t flex justify-between items-center gap-3"
          style={{ borderColor: 'var(--border)', background: 'var(--surface-2)' }}
        >
          <button onClick={onClose} className="btn btn-ghost" disabled={submitting}>
            Cancelar
          </button>
          <button onClick={handleSubmit} disabled={submitting} className="btn btn-primary">
            {submitting
              ? <I.Activity size={14} className="animate-pulse" />
              : <I.Check size={14} />}
            Crear estudiante
          </button>
        </div>
      </aside>

      <style>{`
        @keyframes slide-in-right {
          from { transform: translateX(100%); opacity: 0; }
          to   { transform: translateX(0);    opacity: 1; }
        }
      `}</style>
    </>
  );
};

export default DrawerCrearEstudiante;
